/* eslint-disable complexity */
import React, {
    useEffect,
    useState,
    useMemo,
    useCallback,
    useContext,
} from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    ButtonSecondary,
    HorizontalFlex,
    VerticalFlex,
    PadContainer,
} from 'smartsuite-ui';
import { useLabelContext } from '../shared/hooks/labelContext';
import DashboardContext from '../shared/hooks/dashboardContext';
import {
    getLandingPageWidgetOptions,
    getSavedFilterOptions,
    systemPanelOptions,
} from './landingPageSelectorUtils';
import {
    populateSystemPanel,
    populateWidgetPanel,
} from './landingPageSetupUtils';
import { systemWidgetType } from './landingPageConstants';
import DashboardViewWidgetSelect from './addWidgetSelectors/DashboardViewWidgetSelect';
import FilterSelect from './addWidgetSelectors/FilterSelect';

function isSystemWidgetSelected(selectedDashboardWidget) {
    return (
        !selectedDashboardWidget ||
        selectedDashboardWidget?.type === systemWidgetType
    );
}

export default function LandingPagePanelSelector(props) {
    const { dashboardLabels, translationLabels } = useLabelContext();
    const {
        dashboardConfigs,
        allSavedFilters,
        widgetRenderers,
        selectOptions,
    } = useContext(DashboardContext);

    const systemPanels = useMemo(
        () => systemPanelOptions(dashboardLabels),
        [dashboardLabels]
    );

    const [savedFilterOptions, setSavedFilterOptions] = useState([]);
    const [selectedSavedFilter, setSavedSelectedFilter] = useState(null);

    const landingPageWidgetsOptions = useMemo(
        () => getLandingPageWidgetOptions(dashboardConfigs),
        [dashboardConfigs]
    );
    const [selectedLandingPageWidget, setSelectedLandingPageWidget] = useState(
        {}
    );

    const getFilterOptions = useCallback(
        (dashboardWidget) => {
            const dashboard = dashboardConfigs.find(
                (x) => x.id === dashboardWidget?.valueField.dashboardKey
            );

            if (!dashboard?.configuration?.views?.length) {
                return [];
            }

            const view = dashboard.configuration.views.find(
                (x) => x.viewKey === dashboardWidget?.valueField.viewKey
            );

            return getSavedFilterOptions(
                dashboard.id,
                view,
                allSavedFilters,
                selectOptions,
                dashboardLabels
            );
        },
        [dashboardConfigs, allSavedFilters, dashboardLabels, selectOptions]
    );

    useEffect(() => {
        // eslint-disable-next-line complexity
        async function handleInitialLoad() {
            const systemWidget = systemPanels.find(
                (x) => x.valueField.configurationKey === props.configurationKey
            );
            if (systemWidget) {
                setSelectedLandingPageWidget(systemWidget);
                return;
            }

            if (!props.dashboardKey || !props.viewKey) {
                return;
            }

            const widgetOptionsForDashboardView =
                landingPageWidgetsOptions.find((x) =>
                    x.options.find(
                        (y) =>
                            y.valueField.landingPageWidgetId ===
                            props.landingPageWidgetId
                    )
                );
            const landingPageWidget =
                widgetOptionsForDashboardView?.options.find(
                    (x) =>
                        x.valueField.landingPageWidgetId ===
                        props.landingPageWidgetId
                );

            const options = getFilterOptions(landingPageWidget);

            setSavedFilterOptions(options);
            setSavedSelectedFilter(options.length === 1 ? options[0] : null);
            setSelectedLandingPageWidget(landingPageWidget);
        }

        handleInitialLoad();
    }, [
        landingPageWidgetsOptions,
        getFilterOptions,
        props.configurationKey,
        props.dashboardKey,
        props.landingPageWidgetId,
        props.savedFilterKey,
        props.viewKey,
        systemPanels,
    ]);

    function onSave() {
        const landingPageWidget = selectedLandingPageWidget?.valueField;

        if (landingPageWidget?.type === systemWidgetType) {
            const panel = { panelIndex: props.panelIndex, landingPageWidget };
            props.onSave(populateSystemPanel(panel, widgetRenderers));
            props.closeModal();
            return;
        }

        const panel = {
            panelIndex: props.panelIndex,
            dashboardKey: landingPageWidget.dashboardKey,
            viewKey: landingPageWidget.viewKey,
            landingPageWidgetId: landingPageWidget.landingPageWidgetId,
            savedFilterKey: selectedSavedFilter?.valueField.savedFilterKey,
            savedFilterDescription: selectedSavedFilter?.textField,
        };

        const populatedPanel = populateWidgetPanel(
            panel,
            dashboardConfigs,
            allSavedFilters,
            widgetRenderers,
            selectOptions
        );

        props.onSave(populatedPanel);
        props.closeModal(populatedPanel);
    }

    function isSaveEnabled() {
        return (
            selectedLandingPageWidget?.valueField?.type === systemWidgetType ||
            (selectedLandingPageWidget && selectedSavedFilter)
        );
    }

    // eslint-disable-next-line complexity
    async function onDashboardWidgetSelect(dashboardWidget) {
        const dashboardWidgetValue = dashboardWidget?.valueField;

        if (isSystemWidgetSelected(dashboardWidgetValue)) {
            setSelectedLandingPageWidget(dashboardWidget);
            setSavedFilterOptions([]);
            setSavedSelectedFilter(null);
            return;
        }

        const options = getFilterOptions(dashboardWidget);
        setSelectedLandingPageWidget(dashboardWidget);
        setSavedFilterOptions(options);
        setSavedSelectedFilter(options.length === 1 ? options[0] : null);
    }

    const shouldShowFilterOptions =
        selectedLandingPageWidget &&
        !!Object.keys(selectedLandingPageWidget).length &&
        selectedLandingPageWidget.valueField?.type !== systemWidgetType;

    return (
        <Modal onOverlayClick={props.closeModal}>
            <ModalHeader onDismiss={props.closeModal}>
                {props.modalHeader}
            </ModalHeader>
            <ModalBody>
                <PadContainer cssClassModifiers={['pad-medium']}>
                    <VerticalFlex>
                        <DashboardViewWidgetSelect
                            landingPageWidgetOptions={landingPageWidgetsOptions}
                            onChange={onDashboardWidgetSelect}
                            selectedLandingPageWidget={
                                selectedLandingPageWidget
                            }
                            systemPanels={systemPanels}
                        />
                        <PadContainer cssClassModifiers={['pad-medium']} />
                        {shouldShowFilterOptions && (
                            <FilterSelect
                                savedFilterOptions={savedFilterOptions}
                                selectedDashboardWidget={
                                    selectedLandingPageWidget
                                }
                                selectedFilter={selectedSavedFilter}
                                setSelectedFilter={setSavedSelectedFilter}
                            />
                        )}
                    </VerticalFlex>
                </PadContainer>
                <PadContainer cssClassModifiers={['pad-medium']} />
            </ModalBody>
            <ModalFooter>
                <HorizontalFlex cssClassModifiers={['justify-flex-end']}>
                    {props.onDelete && (
                        <ButtonSecondary
                            onClick={() => props.onDelete(props.panelIndex)}
                        >
                            {translationLabels.delete}
                        </ButtonSecondary>
                    )}
                    <HorizontalFlex
                        cssClassModifiers={[
                            'small-relative',
                            'justify-flex-end',
                        ]}
                    >
                        <Button disabled={!isSaveEnabled()} onClick={onSave}>
                            {translationLabels.save}
                        </Button>
                        <ButtonSecondary onClick={props.closeModal}>
                            {translationLabels.cancel}
                        </ButtonSecondary>
                    </HorizontalFlex>
                </HorizontalFlex>
            </ModalFooter>
        </Modal>
    );
}

LandingPagePanelSelector.propTypes = {
    closeModal: PropTypes.func.isRequired,
    configurationKey: PropTypes.string,
    dashboardKey: PropTypes.string,
    modalHeader: PropTypes.string.isRequired,
    onDelete: PropTypes.func,
    onSave: PropTypes.func.isRequired,
    panelIndex: PropTypes.number.isRequired,
    savedFilterKey: PropTypes.string,
    viewKey: PropTypes.string,
    landingPageWidgetId: PropTypes.string,
};

LandingPagePanelSelector.defaultProps = {
    onDelete: undefined,
};
